import { getPendingImports } from "components/ImportData/utils";
import { useFetchJobsPolling } from "hooks/useJobs";
import { useEffect, useMemo } from "react";
import { fetchActiveJobs, type Job } from "store/jobsStore";
import { setRoyaltiesRecalculating } from "store/managementStore";
import { setRecomputeRoyaltiesStatus } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";

const ROYALTIES_RECALC_JOBS_POLL_MS = 60000;

/**
 * Detects an in-progress period royalties recalculation for the viewed period,
 * including jobs started by another user (via the shared jobs list).
 */
export function useRoyaltiesRecalculationInProgress(periodId: number | undefined) {
    const dispatch = useAppDispatch();
    const jobs = useAppSelector((state) => state.jobs.jobs);
    const recomputeRoyaltiesStatus = useAppSelector((state) => state.period.recomputeRoyaltiesStatus);
    const { recomputeRoyaltiesJobId, recomputeRoyaltiesPeriodId } = useAppSelector((state) => state.management);

    const pendingRoyaltiesRecalcJobs = useMemo(() => {
        if (!periodId) {
            return [];
        }

        return getPendingImports({ jobs, type: ["compute-period-royalties"] }).filter((job): job is Job => {
            if (!("id" in job)) {
                return false;
            }

            return job.metadata?.periodId === periodId;
        });
    }, [jobs, periodId]);

    const activeJob = pendingRoyaltiesRecalcJobs[0];

    const isInProgress =
        pendingRoyaltiesRecalcJobs.length > 0 ||
        (recomputeRoyaltiesStatus === "pending" && recomputeRoyaltiesPeriodId === periodId);

    const pollingJobId =
        recomputeRoyaltiesPeriodId === periodId ? recomputeRoyaltiesJobId ?? activeJob?.id : activeJob?.id;

    useEffect(() => {
        if (!periodId) {
            return;
        }
        dispatch(fetchActiveJobs());
    }, [dispatch, periodId]);

    useFetchJobsPolling({ enabled: isInProgress, intervalMs: ROYALTIES_RECALC_JOBS_POLL_MS });

    useEffect(() => {
        if (!periodId || !activeJob) {
            return;
        }

        if (recomputeRoyaltiesJobId === activeJob.id && recomputeRoyaltiesPeriodId === periodId) {
            return;
        }

        dispatch(setRoyaltiesRecalculating({ jobId: activeJob.id, periodId }));
        if (recomputeRoyaltiesStatus !== "pending") {
            dispatch(setRecomputeRoyaltiesStatus("pending"));
        }
    }, [activeJob, dispatch, periodId, recomputeRoyaltiesJobId, recomputeRoyaltiesPeriodId, recomputeRoyaltiesStatus]);

    return {
        isInProgress,
        pollingJobId,
    };
}
